import { number_format, on } from "../util";

// ---------------------------------------------------------------------------
// D-08/D-09/D-10 — item-page crafting calculator client half. Consumes the
// cycle-safe all-methods tree emitted by items_single.latte's
// `var eo_crafting_tree = {...}` global (209-05's craftingSteps()/
// getAllItemMethods()). Named `crafting-calc.ts` (NOT `crafting.ts`, which is
// the unrelated recipe-EDITOR page) per the plan's explicit naming note.
// ---------------------------------------------------------------------------

declare var eo_crafting_tree: EoCraftingNode | undefined;

interface EoCraftingMethod {
    method: string;
    min: number;
    max: number;
}

interface EoCraftingNode {
    id: number | null;
    items_id: number;
    name: string | null;
    amount: number;
    min: number;
    max: number;
    method: string;
    methods: EoCraftingMethod[];
    steps: EoCraftingNode[];
}

interface EoCraftingTotal {
    id: number;
    name: string;
    amount: number;
    min: number;
    max: number;
}

export default class {
    static init = () => {
        craftingCalc();
    }
}

function badgeClass(method : string) : string {
    if(method === 'crafting') {
        return 'primary';
    } else if(method === 'trade') {
        return 'success';
    }
    return 'danger';
}

// Mirrors the Latte block's own number_format(...) . 'g' [- 'g'] range idiom
// (D-10) — gold has no decimal places, matching PHP's number_format() default.
function formatPrice(min : number, max : number) : string {
    if(min === max) {
        return number_format(min, 0) + 'g';
    }
    return number_format(min, 0) + 'g - ' + number_format(max, 0) + 'g';
}

function craftingCalc() {
    let root = document.querySelector('.eo-crafting-root') as HTMLElement;
    if(!root) {
        return;
    }
    if(typeof eo_crafting_tree === 'undefined' || !eo_crafting_tree) {
        return;
    }

    let tree = eo_crafting_tree;

    // Per-node current selection, defaulted to the server-resolved cheapest
    // method, keyed by step.id (eo_crafting_materials.id). NOTE: that id is NOT
    // unique within a render — a shared intermediate material re-expands once per
    // parent path (see the querySelectorAll lockstep handling in render(), WR-03) —
    // but a material's chosen acquisition method is intentionally shared across all
    // of its occurrences. Cycle-marker nodes (id === null) are excluded — they are
    // terminal and never selectable.
    let nodesById = new Map<number, EoCraftingNode>();
    let selections = new Map<number, string>();

    function indexNode(node : EoCraftingNode) {
        (node.steps || []).forEach((step) => {
            if(step.method === 'cycle' || step.id === null) {
                return;
            }
            nodesById.set(step.id, step);
            selections.set(step.id, step.method);
            indexNode(step);
        });
    }
    indexNode(tree);

    // D-09/D-10: integer-gold re-sum. Walks the tree honoring the CURRENT
    // selections, summing min*amount and max*amount as two separate running
    // integer totals — no mean, no division, anywhere in this path. A node
    // whose selection is 'crafting' but whose steps array is empty (the
    // server only recurses into its cheapest-resolved branch — an alternate
    // 'crafting' choice on a non-cheapest node may have no pre-fetched
    // sub-recipe) falls back to its own resolved crafting total as a leaf
    // contribution rather than silently dropping the cost.
    function computeTotals() : { totals : Map<number, EoCraftingTotal>, min : number, max : number } {
        let totals = new Map<number, EoCraftingTotal>();

        function walk(node : EoCraftingNode, runningAmount : number) {
            (node.steps || []).forEach((step) => {
                if(step.method === 'cycle' || step.id === null) {
                    return;
                }
                let selectedMethod = selections.get(step.id) ?? step.method;
                let entry = step.methods.find((m) => m.method === selectedMethod) ?? step.methods[0];
                let min = entry ? entry.min : 0;
                let max = entry ? entry.max : 0;
                let itemAmount = runningAmount * step.amount;

                if(selectedMethod === 'crafting' && step.steps && step.steps.length > 0) {
                    walk(step, itemAmount);
                } else {
                    if(!totals.has(step.items_id)) {
                        totals.set(step.items_id, {id: step.items_id, name: step.name ?? '', amount: 0, min: 0, max: 0});
                    }
                    let t = totals.get(step.items_id) as EoCraftingTotal;
                    t.amount += itemAmount;
                    t.min += min * itemAmount;
                    t.max += max * itemAmount;
                }
            });
        }

        walk(tree, tree.amount);

        let grandMin = 0;
        let grandMax = 0;
        totals.forEach((t) => {
            grandMin += t.min;
            grandMax += t.max;
        });

        return { totals: totals, min: grandMin, max: grandMax };
    }

    function render() {
        let result = computeTotals();

        let totalsList = document.querySelector('.js-eo-craft-totals') as HTMLElement;
        if(totalsList) {
            totalsList.innerHTML = '';
            Array.from(result.totals.values())
                .sort((a, b) => a.id - b.id)
                .forEach((t) => {
                    // WR-02: build via DOM nodes, not innerHTML string concat — t.name
                    // is item data and MUST NOT be interpreted as markup (the emitting
                    // JSON's JSON_HEX_* flags only protect the <script> source context;
                    // once parsed into a JS string those escapes decode back to literal
                    // HTML). This restores the auto-escape discipline of the server Latte.
                    let li = document.createElement('li');

                    let img = document.createElement('img');
                    img.src = '/assets/eo/' + eo_version + '/items/' + t.id + '.png';
                    img.className = 'eo-item-image-small';

                    let link = document.createElement('a');
                    link.href = '/eo/items/' + t.id;
                    link.textContent = t.name;

                    let price = document.createElement('span');
                    price.className = 'text-success';
                    price.textContent = formatPrice(t.min, t.max);

                    li.append(img, ' ', link, ' x ' + t.amount + ' ', price);
                    totalsList.appendChild(li);
                });
        }

        let grandTotalEl = document.querySelector('.js-eo-craft-grand-total') as HTMLElement;
        if(grandTotalEl) {
            grandTotalEl.textContent = formatPrice(result.min, result.max);
        }

        // WR-03: eo_crafting_materials.id is NOT unique per render — craftingSteps()
        // re-expands a shared intermediate material once per parent path, so the same
        // id can appear on several DOM occurrences. Update EVERY occurrence in lockstep
        // (querySelectorAll, not querySelector) so no duplicate's badge/price/select
        // goes stale relative to the value used in the recomputed total.
        nodesById.forEach((node, id) => {
            let selectedMethod = selections.get(id) ?? node.method;
            let entry = node.methods.find((m) => m.method === selectedMethod) ?? node.methods[0];
            let min = entry ? entry.min : 0;
            let max = entry ? entry.max : 0;
            let selector = '[data-node-id="' + id + '"]';
            let hasContent = !!(node.steps && node.steps.length > 0);

            document.querySelectorAll('.js-eo-craft-badge' + selector).forEach((el) => {
                el.className = 'badge text-bg-' + badgeClass(selectedMethod) + ' js-eo-craft-badge';
                el.setAttribute('data-node-id', String(id));
                el.textContent = selectedMethod;
            });

            document.querySelectorAll('.js-eo-craft-price' + selector).forEach((el) => {
                el.textContent = formatPrice(min * node.amount, max * node.amount);
            });

            // D-09: a node's 'crafting' method can be selected even when the server only
            // pre-fetched a DIFFERENT (cheapest-resolved) branch's sub-recipe — that node's
            // own steps array is empty (node.steps.length === 0 / !node.steps). The total
            // already falls back correctly to a leaf contribution (see walk()/computeTotals()
            // above), but the substeps <div> used to stay empty with no explanation, reading
            // as "nothing happened" even though nothing is actually wrong. Surface a one-line
            // note in its place instead. Built via document.createElement/textContent (WR-02
            // discipline, same as render()'s totals-list above) — no string-concatenated
            // markup assignment anywhere in this path, since node names are item data.
            let hasEmptySteps = !node.steps || node.steps.length === 0;
            let showEmptyNote = selectedMethod === 'crafting' && hasEmptySteps;

            document.querySelectorAll('.js-eo-craft-substeps' + selector).forEach((el) => {
                el.classList.toggle('d-none', !(selectedMethod === 'crafting' && (hasContent || hasEmptySteps)));

                let note = el.querySelector('.js-eo-craft-empty-note') as HTMLElement | null;
                if(showEmptyNote) {
                    if(!note) {
                        note = document.createElement('div');
                        note.className = 'text-muted small js-eo-craft-empty-note';
                        note.textContent = 'resolved from base cost — no sub-recipe breakdown';
                        el.appendChild(note);
                    }
                } else if(note) {
                    note.remove();
                }
            });

            document.querySelectorAll('.js-eo-craft-method-select' + selector).forEach((el) => {
                (el as HTMLSelectElement).value = selectedMethod;
            });
        });
    }

    on('change', '.js-eo-craft-method-select', (target : HTMLSelectElement) => {
        let nodeId = parseInt(target.getAttribute('data-node-id') ?? '0', 10);
        if(!isNaN(nodeId) && nodeId > 0) {
            selections.set(nodeId, target.value);
        }
        render();
    });

    render();
}

declare const eo_version : string;
